/**
 * Djay Presence — types & policy constants for the proactive-speech budget.
 *
 * App-Feel 2027 §3B / §5.2 seam #1 — "Djay is Aanwezig, met Cadans".
 *
 * Problem (validated against FloatingDjay.tsx): five INDEPENDENT proactive
 * (unsolicited) speech triggers — welcome, view-greet, error-tip, time-egg,
 * idle — each fired a bubble + `mascot.speak` with NO budget, NO priority and
 * NO cooldown between them. The only restraint was a binary `home` suppression
 * and W1's 900ms audio debounce (which silences the *sound* but not the
 * *decision to speak*). Cumulatively: "Djay praat te veel".
 *
 * This layer makes Djay's VOICE a budgeted, prioritized, observable resource —
 * exactly the anti-bandaid discipline of ARCHITECTURAL_PEER_REVIEW_PLAN_2026
 * (#2 enums over booleans, #3 a named/inspectable budget instead of a silent
 * `return`, #4 cadence observable in code). Interactive speech (tap, hover,
 * easter eggs) is NOT governed here — only proactive utterances.
 *
 * DDD: pure + framework-free. No React, no timers, no side-effects. The hook
 * (`usePresenceOrchestrator`) and the view (`FloatingDjay`) consume it.
 */

import { DJAY_TIMINGS } from '../config'

// ── Proactive utterance kinds, in PRIORITY ORDER ──────────────
// Lower index = higher priority. When two proactive requests compete inside
// the same cooldown window, the higher-priority one wins; the loser is dropped
// (proactive speech is ephemeral — a stale greeting is worse than silence).
export type ProactiveKind =
  | 'errorTip' // just-in-time misconception nudge — most pedagogically valuable
  | 'viewGreet' // contextual "welcome to <view>" on navigation
  | 'welcome' // first-entry greeting this session
  | 'timeEgg' // nacht-uil / vroege-vogel delight
  | 'idle' // "still there?" nudge — lowest value, most interrupting

/** Canonical priority ranking. Index 0 = highest. */
export const PROACTIVE_PRIORITY: readonly ProactiveKind[] = [
  'errorTip',
  'viewGreet',
  'welcome',
  'timeEgg',
  'idle',
] as const

export function priorityOf(kind: ProactiveKind): number {
  const i = PROACTIVE_PRIORITY.indexOf(kind)
  return i === -1 ? PROACTIVE_PRIORITY.length : i
}

/** A request for proactive speak-time. Triggers ASK; the orchestrator decides. */
export interface PresenceRequest {
  kind: ProactiveKind
  /** Timestamp (performance.now()/Date.now()) the request was made. */
  at: number
}

// ── Presence budget policy (the cadence SSOT) ─────────────────
export const PRESENCE_POLICY = {
  /**
   * Max proactive utterances Djay may make per session. Beyond this he stays
   * present (visible, tappable) but silent until the next session. Keeps the
   * "aanwezig maar niet opdringerig" feel (§8). Empirically tunable (§9 vraag 3).
   */
  MAX_PER_SESSION: 4,
  /**
   * Minimum gap between any two proactive utterances. This is the PRESENCE
   * cadence (the decision to speak), distinct from and longer than the W1
   * audio cadence (the 900ms `mascot.speak` debounce in soundIntent.ts). Two
   * different triggers firing back-to-back collapse to the higher-priority one.
   */
  MIN_GAP_MS: 45 * 1000,
} as const

// ── Per-kind bubble presentation (extracted from FloatingDjay) ─
/** Each proactive kind's bubble dwell-time, sourced from DJAY_TIMINGS (SSOT). */
export const PROACTIVE_BUBBLE_MS: Record<ProactiveKind, number> = {
  errorTip: DJAY_TIMINGS.ERROR_TIP_BUBBLE_MS,
  viewGreet: DJAY_TIMINGS.VIEW_CHANGE_BUBBLE_MS,
  welcome: DJAY_TIMINGS.WELCOME_BUBBLE_MS,
  timeEgg: DJAY_TIMINGS.TIME_EGG_BUBBLE_MS,
  idle: DJAY_TIMINGS.IDLE_BUBBLE_MS,
}
